import { FootballGame, GameStatus } from '@/types';

const BASE_URL = 'https://site.api.espn.com/apis/site/v2/sports/soccer';

// Mapeamento: league id (api-sports.io) → slug ESPN
const ESPN_LEAGUE_SLUGS: Record<string, string> = {
  '1':   'fifa.world',             // Copa do Mundo FIFA
  '39':  'eng.1',                  // Premier League
  '140': 'esp.1',                  // La Liga
  '135': 'ita.1',                  // Serie A
  '78':  'ger.1',                  // Bundesliga
  '61':  'fra.1',                  // Ligue 1
  '2':   'uefa.champions',         // Champions League
  '3':   'uefa.europa',            // Europa League
  '71':  'bra.1',                  // Brasileirão Série A
  '73':  'bra.cup',                // Copa do Brasil
  '128': 'conmebol.libertadores',  // Copa Libertadores
};

const ESPN_LEAGUE_NAMES: Record<string, string> = {
  'fifa.world': 'Copa do Mundo FIFA',
  'eng.1': 'Premier League',
  'esp.1': 'La Liga',
  'ita.1': 'Serie A',
  'ger.1': 'Bundesliga',
  'fra.1': 'Ligue 1',
  'uefa.champions': 'UEFA Champions League',
  'uefa.europa': 'UEFA Europa League',
  'bra.1': 'Brasileirão Série A',
  'bra.cup': 'Copa do Brasil',
  'conmebol.libertadores': 'Copa Libertadores',
};

const cache = new Map<string, { data: unknown; ts: number }>();

function cached<T>(key: string, ttl: number, fetcher: () => Promise<T>): Promise<T> {
  const entry = cache.get(key);
  if (entry && Date.now() - entry.ts < ttl) return Promise.resolve(entry.data as T);
  return fetcher().then((data) => {
    cache.set(key, { data, ts: Date.now() });
    return data;
  });
}

function mapStatus(event: any): GameStatus {
  const state = event.status?.type?.state ?? '';
  const name = event.status?.type?.name ?? '';
  if (state === 'post' || event.status?.type?.completed) return 'finished';
  if (state === 'in') {
    if (name === 'STATUS_HALFTIME') return 'halftime';
    return 'live';
  }
  return 'scheduled';
}

function mapGame(event: any, leagueSlug: string, leagueId: string): FootballGame {
  const comp = event.competitions?.[0] ?? event;
  const competitors = comp.competitors ?? [];
  const home = competitors.find((c: any) => c.homeAway === 'home');
  const away = competitors.find((c: any) => c.homeAway === 'away');
  const status = event.status ?? comp.status;

  const detail = status?.type?.shortDetail ?? '';
  const minuteMatch = detail.match(/^(\d+)'/);
  const minute = minuteMatch ? parseInt(minuteMatch[1], 10) : undefined;

  return {
    id: String(event.id),
    sport: 'futebol',
    league: {
      id: leagueId,
      name: ESPN_LEAGUE_NAMES[leagueSlug] ?? leagueSlug,
      logo: `https://a.espncdn.com/i/leaguelogos/soccer/500/${leagueSlug}.png`,
      country: '',
      flag: '',
    },
    homeTeam: {
      id: String(home?.team?.id ?? ''),
      name: home?.team?.displayName ?? home?.team?.name ?? '',
      shortName: home?.team?.abbreviation ?? '',
      logo: home?.team?.logo ?? '',
    },
    awayTeam: {
      id: String(away?.team?.id ?? ''),
      name: away?.team?.displayName ?? away?.team?.name ?? '',
      shortName: away?.team?.abbreviation ?? '',
      logo: away?.team?.logo ?? '',
    },
    score: {
      home: parseInt(home?.score ?? '0', 10),
      away: parseInt(away?.score ?? '0', 10),
    },
    status: mapStatus(event),
    minute,
    startTime: event.date ?? comp.startDate ?? '',
    venue: comp.venue?.fullName,
    events: [],
  };
}

async function fetchLeague(slug: string, leagueId: string, date: string): Promise<FootballGame[]> {
  try {
    const res = await fetch(`${BASE_URL}/${slug}/scoreboard?dates=${date}`);
    if (!res.ok) return [];
    const json = await res.json();
    const events: any[] = json.events ?? [];
    return events.map((e) => mapGame(e, slug, leagueId));
  } catch {
    return [];
  }
}

export async function getESPNFootballTodayGames(): Promise<FootballGame[]> {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return cached(`espn:football:today:${today}`, 60_000, async () => {
    const entries = Object.entries(ESPN_LEAGUE_SLUGS);
    const results = await Promise.all(
      entries.map(([leagueId, slug]) => fetchLeague(slug, leagueId, today))
    );
    const all = results.flat();
    console.log(`[ESPN Soccer] Total jogos hoje: ${all.length}`);
    return all;
  });
}

// Busca TODOS os jogos da Copa do Mundo 2026 (grupos + fases eliminatórias)
// Copa 2026: 11 jun – 26 jul de 2026, 48 seleções, 104 jogos
export async function getWorldCupAllGames(): Promise<FootballGame[]> {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const cacheKey = `espn:worldcup:2026:${today}`;
  return cached(cacheKey, 300_000, async () => {
    // Tenta buscar com range de datas primeiro (retorna todos os jogos do torneio)
    const attempts = [
      `${BASE_URL}/fifa.world/scoreboard?dates=20260611-20260726&limit=200`,
      `${BASE_URL}/fifa.world/scoreboard?limit=200`,
      `${BASE_URL}/fifa.world/scoreboard`,
    ];

    for (const url of attempts) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const json = await res.json();
        const events: any[] = json.events ?? [];
        if (events.length > 0) {
          console.log(`[ESPN World Cup] ${events.length} jogos (${url.split('?')[1] ?? 'default'})`);
          return events.map((e) => mapGame(e, 'fifa.world', '1'));
        }
      } catch {}
    }
    console.warn('[ESPN World Cup] Nenhum resultado encontrado');
    return [];
  });
}

// Jogos da Copa do Mundo de hoje — cache curto (30s) para placar em tempo real
export async function getWorldCupTodayGames(): Promise<FootballGame[]> {
  const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return cached(`espn:worldcup:today:${today}`, 30_000, async () => {
    try {
      const res = await fetch(`${BASE_URL}/fifa.world/scoreboard?dates=${today}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const events: any[] = json.events ?? [];
      return events.map((e) => mapGame(e, 'fifa.world', '1'));
    } catch {
      const all = await getWorldCupAllGames();
      const todayStr = new Date().toISOString().slice(0, 10);
      return all.filter((g) => g.startTime.startsWith(todayStr));
    }
  });
}

// Jogos ao vivo da Copa do Mundo
export async function getWorldCupLiveGames(): Promise<FootballGame[]> {
  const today = await getWorldCupTodayGames();
  return today.filter((g) => g.status === 'live' || g.status === 'halftime');
}

// ─── Classificação dos grupos ─────────────────────────────────────────────────

export interface GroupTeamRow {
  rank: number;
  teamId: string;
  teamName: string;
  teamShortName: string;
  teamLogo: string;
  played: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
  points: number;
}

export interface CopaGroup {
  id: string;
  name: string;   // "Grupo A", "Grupo B", etc.
  teams: GroupTeamRow[];
}

function parseGroupTeam(entry: any): GroupTeamRow {
  const team = entry.team ?? {};
  const stats: Record<string, number> = {};
  (entry.stats ?? []).forEach((s: any) => {
    stats[s.name] = Number(s.value ?? 0);
  });
  return {
    rank: Number(entry.rank ?? entry.position ?? 0),
    teamId: String(team.id ?? ''),
    teamName: team.displayName ?? team.name ?? '',
    teamShortName: team.abbreviation ?? '',
    teamLogo: team.logos?.[0]?.href ?? team.logo ?? '',
    played: stats['gamesPlayed'] ?? 0,
    wins: stats['wins'] ?? 0,
    draws: stats['ties'] ?? 0,
    losses: stats['losses'] ?? 0,
    goalsFor: stats['pointsFor'] ?? stats['goals'] ?? 0,
    goalsAgainst: stats['pointsAgainst'] ?? 0,
    goalDiff: stats['differential'] ?? 0,
    points: stats['points'] ?? 0,
  };
}

export async function getWorldCupStandings(): Promise<CopaGroup[]> {
  return cached('espn:worldcup:standings', 300_000, async () => {
    try {
      const res = await fetch(`${BASE_URL}/fifa.world/standings`);
      if (!res.ok) throw new Error(`ESPN Standings HTTP ${res.status}`);
      const json = await res.json();

      const groups: CopaGroup[] = [];
      const children: any[] = json.children ?? json.standings ?? [];

      for (const child of children) {
        // Cada "child" é um grupo
        const groupName = child.name ?? child.abbreviation ?? '';
        const entries: any[] = child.standings?.entries ?? child.entries ?? [];
        if (entries.length === 0) continue;

        groups.push({
          id: String(child.id ?? groupName),
          name: groupName,
          teams: entries.map(parseGroupTeam).sort((a, b) => a.rank - b.rank),
        });
      }

      console.log(`[ESPN Copa Standings] ${groups.length} grupos`);
      return groups;
    } catch (err) {
      console.warn('[ESPN Copa Standings] Erro:', err);
      return [];
    }
  });
}
